import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import ClientLayout from '../../components/ClientLayout';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Plus, Search, Briefcase, MoreHorizontal, Edit, Play, CheckCircle, XCircle } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import axios from 'axios';

const JobsPage = () => {
  const { token, API } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [crews, setCrews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    customer_id: '',
    property_id: '',
    job_type: 'one_time',
    scheduled_date: '',
    scheduled_time: '',
    estimated_duration: '',
    crew_id: '',
    notes: '',
    price: ''
  });

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  const fetchData = async () => {
    try {
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const [jobsRes, customersRes, propertiesRes, crewsRes] = await Promise.all([
        axios.get(`${API}/jobs${params}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/customers`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/properties`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/crews`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setJobs(jobsRes.data.jobs);
      setCustomers(customersRes.data.customers);
      setProperties(propertiesRes.data.properties);
      setCrews(crewsRes.data.crews);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.customer_id || !formData.property_id || !formData.scheduled_date) {
      toast.error('Customer, property, and date are required');
      return;
    }

    const submitData = {
      ...formData,
      estimated_duration: formData.estimated_duration ? parseInt(formData.estimated_duration) : null,
      price: formData.price ? parseFloat(formData.price) : null,
    };

    try {
      await axios.post(`${API}/jobs`, submitData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Job created successfully');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create job');
    }
  };

  const handleStatusChange = async (jobId, newStatus) => {
    try {
      const updateData = { status: newStatus };
      if (newStatus === 'in_progress') {
        updateData.actual_start_time = new Date().toISOString();
      } else if (newStatus === 'completed') {
        updateData.actual_end_time = new Date().toISOString();
      }
      
      await axios.put(`${API}/jobs/${jobId}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Job ${newStatus.replace('_', ' ')}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update job');
    }
  };

  const resetForm = () => {
    setFormData({
      customer_id: '',
      property_id: '',
      job_type: 'one_time',
      scheduled_date: '',
      scheduled_time: '',
      estimated_duration: '',
      crew_id: '',
      notes: '',
      price: ''
    });
  };

  const getCustomerName = (id) => customers.find(c => c.id === id)?.name || 'Unknown';
  const getPropertyAddress = (id) => properties.find(p => p.id === id)?.address || 'Unknown';
  const getCrewName = (id) => crews.find(c => c.id === id)?.name || 'Unassigned';
  const filteredProperties = properties.filter(p => p.customer_id === formData.customer_id);

  const filteredJobs = jobs.filter(j =>
    getCustomerName(j.customer_id).toLowerCase().includes(searchQuery.toLowerCase()) ||
    getPropertyAddress(j.property_id).toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusBadge = (status) => {
    const config = {
      scheduled: { variant: 'secondary', className: 'bg-blue-100 text-blue-800' },
      in_progress: { variant: 'secondary', className: 'bg-amber-100 text-amber-800' },
      completed: { variant: 'default', className: 'bg-emerald-100 text-emerald-800' },
      cancelled: { variant: 'destructive', className: 'bg-red-100 text-red-800' }
    };
    const { className } = config[status] || config.scheduled;
    return <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${className}`}>{status.replace('_', ' ')}</span>;
  };

  return (
    <ClientLayout title="Jobs">
      <Card className="border-gray-200">
        <CardHeader>
          <div className="flex flex-col sm:flex-row gap-4 justify-between">
            <div className="flex gap-4 flex-1">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search jobs..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-jobs-input"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-36">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="scheduled">Scheduled</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-primary hover:bg-primary/90" data-testid="add-job-btn">
                  <Plus className="h-4 w-4 mr-2" /> Create Job
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Create New Job</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Customer *</Label>
                      <Select 
                        value={formData.customer_id} 
                        onValueChange={(value) => setFormData({ ...formData, customer_id: value, property_id: '' })}
                      >
                        <SelectTrigger data-testid="job-customer-select">
                          <SelectValue placeholder="Select customer" />
                        </SelectTrigger>
                        <SelectContent>
                          {customers.map((customer) => (
                            <SelectItem key={customer.id} value={customer.id}>{customer.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Property *</Label>
                      <Select 
                        value={formData.property_id} 
                        onValueChange={(value) => setFormData({ ...formData, property_id: value })}
                        disabled={!formData.customer_id}
                      >
                        <SelectTrigger data-testid="job-property-select">
                          <SelectValue placeholder="Select property" />
                        </SelectTrigger>
                        <SelectContent>
                          {filteredProperties.map((property) => (
                            <SelectItem key={property.id} value={property.id}>{property.address}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Scheduled Date *</Label>
                      <Input
                        type="date"
                        value={formData.scheduled_date}
                        onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                        data-testid="job-date-input"
                      />
                    </div>
                    <div>
                      <Label>Time</Label>
                      <Input
                        type="time"
                        value={formData.scheduled_time}
                        onChange={(e) => setFormData({ ...formData, scheduled_time: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Crew</Label>
                      <Select 
                        value={formData.crew_id} 
                        onValueChange={(value) => setFormData({ ...formData, crew_id: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Assign crew" />
                        </SelectTrigger>
                        <SelectContent>
                          {crews.map((crew) => (
                            <SelectItem key={crew.id} value={crew.id}>{crew.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Est. Duration (min)</Label>
                      <Input
                        type="number"
                        value={formData.estimated_duration}
                        onChange={(e) => setFormData({ ...formData, estimated_duration: e.target.value })}
                        placeholder="60"
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Price ($)</Label>
                    <Input
                      type="number"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      placeholder="150.00"
                    />
                  </div>

                  <div>
                    <Label>Notes</Label>
                    <Textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Special instructions..."
                      rows={2}
                    />
                  </div>

                  <Button className="w-full bg-primary hover:bg-primary/90" onClick={handleSubmit} data-testid="save-job-btn">
                    Create Job
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : filteredJobs.length === 0 ? (
            <div className="text-center py-12">
              <Briefcase className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">No jobs found</p>
              <Button variant="outline" onClick={() => setDialogOpen(true)}>
                Create Your First Job
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Property</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Customer</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Crew</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Date</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Status</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Price</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredJobs.map((job) => (
                    <tr key={job.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`job-${job.id}`}>
                      <td className="py-3 px-4 font-medium text-gray-900">{getPropertyAddress(job.property_id)}</td>
                      <td className="py-3 px-4 text-gray-600">{getCustomerName(job.customer_id)}</td>
                      <td className="py-3 px-4 text-gray-600">{job.crew_id ? getCrewName(job.crew_id) : '-'}</td>
                      <td className="py-3 px-4 text-gray-600 text-sm">
                        {job.scheduled_date} {job.scheduled_time && `@ ${job.scheduled_time}`}
                      </td>
                      <td className="py-3 px-4">{getStatusBadge(job.status)}</td>
                      <td className="py-3 px-4 font-medium text-gray-900">{job.price ? `$${job.price}` : '-'}</td>
                      <td className="py-3 px-4 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm"><MoreHorizontal className="h-4 w-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {job.status === 'scheduled' && (
                              <DropdownMenuItem onClick={() => handleStatusChange(job.id, 'in_progress')}>
                                <Play className="h-4 w-4 mr-2" /> Start Job
                              </DropdownMenuItem>
                            )}
                            {job.status === 'in_progress' && (
                              <DropdownMenuItem onClick={() => handleStatusChange(job.id, 'completed')}>
                                <CheckCircle className="h-4 w-4 mr-2" /> Complete Job
                              </DropdownMenuItem>
                            )}
                            {(job.status === 'scheduled' || job.status === 'in_progress') && (
                              <DropdownMenuItem onClick={() => handleStatusChange(job.id, 'cancelled')} className="text-red-600">
                                <XCircle className="h-4 w-4 mr-2" /> Cancel Job
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </ClientLayout>
  );
};

export default JobsPage;
